import clsx from 'clsx';
import Autocomplete, { AutocompleteProps } from '@material-ui/lab/Autocomplete';

import './ProductServiceFilterSearchPanel.css';
import { FILTER_TREE_TYPE } from 'constants/filter';
import { noop } from 'util/function';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';
import FilterSearchPanel, { FilterSearchPanelProps } from 'components/panel/filter/FilterSearchPanel';
import TextField from 'components/input/TextField';

//
// --- Types ---

export interface ProductServiceFilterSearchPanelProps extends FilterSearchPanelProps {}

interface ProductServiceAutoCompleteProps
  extends Omit<
    AutocompleteProps<TransformedFilterTreeNode, true, true, false>,
    'autoHighlight' | 'disableClearable' | 'getOptionLabel' | 'multiple' | 'options' | 'renderInput' | 'renderTags'
  > {
  flatTreeNodes: ReadonlyArray<TransformedFilterTreeNode>;
  nodeData?: TransformedFilterTreeNode;
}

//
// --- Helper Functions ---

export function getFlattenedTreeNodeListWithNestedNames(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  prefix: string = ''
): ReadonlyArray<TransformedFilterTreeNode> {
  if (!Array.isArray(treeNodes) || !treeNodes.length) {
    return [];
  }

  return treeNodes.reduce<ReadonlyArray<TransformedFilterTreeNode>>((flatTreeNodes, currentTreeNode) => {
    // generate tree node names like "Boilers > Boiler Cleaning > Boiler Water Lance" for nodes

    // skip top level "Product and Services node" (it is not selectable and pollutes tree node names)
    if (currentTreeNode.name.toLowerCase() === 'products and services') {
      return getFlattenedTreeNodeListWithNestedNames(currentTreeNode.children);
    }

    // generate new node name for current node with nested prefix labels (if provided)
    const currentTreeNodeWithUpdatedName = {
      ...currentTreeNode,
      name: prefix ? `${prefix} > ${currentTreeNode.name}` : currentTreeNode.name
    };

    if (Array.isArray(currentTreeNode.children) && currentTreeNode.children.length) {
      return flatTreeNodes.concat(
        [currentTreeNodeWithUpdatedName],
        ...getFlattenedTreeNodeListWithNestedNames(
          currentTreeNodeWithUpdatedName.children,
          currentTreeNodeWithUpdatedName.name
        )
      );
    } else {
      return flatTreeNodes.concat([currentTreeNodeWithUpdatedName]);
    }
  }, []);
}

//
// --- Helper Components ---

const getOptionLabel = (option: TransformedFilterTreeNode) => option?.name || '';
const getOptionSelected = (option: TransformedFilterTreeNode, currentSelectedValue: TransformedFilterTreeNode) =>
  option?.checked || false;
const renderNull = () => null;

const ProductServiceAutoComplete: React.FC<ProductServiceAutoCompleteProps> = props => {
  const { classes = {}, flatTreeNodes, nodeData, ...remainingAutoCompleteProps } = props;

  if (!Array.isArray(flatTreeNodes) || !flatTreeNodes.length) {
    return null;
  }

  return (
    <Autocomplete
      classes={{ ...classes, listbox: clsx('product-service-filter-listbox', classes?.listbox) }}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      options={[...flatTreeNodes]}
      renderInput={params => (
        <TextField
          {...params}
          className="product-service-filter-autocomplete-textfield"
          placeholder="Add a product filter"
          size="small"
        />
      )}
      renderTags={renderNull}
      value={flatTreeNodes.filter(treeNode => treeNode.checked)}
      autoHighlight
      disableClearable
      multiple
      {...remainingAutoCompleteProps}
    />
  );
};

//
// --- ProductServiceFilterSearchPanel Component ---

const ProductServiceFilterSearchPanel: React.FC<ProductServiceFilterSearchPanelProps> = props => {
  const { className, filterTreeData, onFilterCheckedChange = noop, render, ...remainingFilterSearchPanelProps } = props;

  const [productServiceFilterTreeNode] = filterTreeData;
  const hasTreeData = !!productServiceFilterTreeNode;
  return (
    <FilterSearchPanel
      className={clsx('product-service-filter-search-panel', className)}
      data-testid="product-service-filter-search-panel"
      render={render}
      filterTreeData={
        hasTreeData
          ? [
              {
                ...productServiceFilterTreeNode,
                children: [
                  {
                    checked: false,
                    children: [],
                    component: (
                      <ProductServiceAutoComplete
                        aria-label="add product"
                        className="product-service-filter-tree-autocomplete"
                        flatTreeNodes={
                          hasTreeData ? getFlattenedTreeNodeListWithNestedNames([productServiceFilterTreeNode]) : []
                        }
                        id="product-service-filter-tree-autocomplete"
                        onChange={(event, newSelectedTreeNodes, reason, details) => {
                          const changedTreeNode = details?.option || null;

                          if (!changedTreeNode) {
                            return;
                          }

                          if (reason === 'select-option') {
                            onFilterCheckedChange(changedTreeNode.id, true);
                          } else if (reason === 'remove-option') {
                            onFilterCheckedChange(changedTreeNode.id, false);
                          }
                        }}
                      />
                    ),
                    filterTreeType: FILTER_TREE_TYPE.ITEM,
                    id: `${productServiceFilterTreeNode.id}-autocomplete`,
                    leaf: true,
                    name: 'hi',
                    open: false,
                    parentId: productServiceFilterTreeNode.id
                  },
                  ...productServiceFilterTreeNode.children
                ]
              }
            ]
          : []
      }
      onFilterCheckedChange={onFilterCheckedChange}
      {...remainingFilterSearchPanelProps}
    />
  );
};

export default ProductServiceFilterSearchPanel;
